import React from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { ItemsProvider, useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import { WithId } from '@common/typescript/objects/WithId';

import CustomRequestProvider from '@app/components/CustomRequestProvider/CustomRequestProvider';
import FaqBlock from '@app/components/UI/FaqBlock/FaqBlock';
import HelmetProvider from '@app/components/UI/HelmetProvider/HelmetProvider';
import LoadMoreButton from '@app/components/UI/LoadMoreButton/LoadMoreButton';
import { Issues, Questions } from '@app/objects/Questions';

type IssuesId = Issues & WithId;

interface FRFaqProps {
	setCategoriesFAQ: (e: Array<Issues>) => void;
	currentCategoryFAQ?: Issues;
	setCurrentCategoryFAQ: (e: Issues) => void;
	searchQuery: string | undefined;
}

interface CategoryQuestionsProps {
	searchQuery: string | undefined;
	categoriesFAQ: Array<Issues>;
	currentCategoryFAQ: Issues | undefined;
	showCategory: (e: Issues | undefined) => void;
}

interface CertainCategoryFAQProps {
	currentCategoryFAQ: Issues;
	questions: Array<Questions>;
}

interface AllCategoriesFAQProps {
	categoriesFAQ: Array<Issues>;
	showCategory: (e: Issues | undefined) => void;
}

const CertainCategoryFAQ: React.FC<CertainCategoryFAQProps> = ({ currentCategoryFAQ, questions }) => {
	return (
		<>
			<header className="faq-block-header">{currentCategoryFAQ.name}</header>
			{questions.length ? (
				<FaqBlock questions={questions} expandIconPosition="right" />
			) : (
				<div className="faq-block__dummy">Woof! We were not able to find a match. Please try another search.</div>
			)}
		</>
	);
};

const AllCategoriesFAQ: React.FC<AllCategoriesFAQProps> = ({ categoriesFAQ, showCategory }) => {
	return (
		<>
			{categoriesFAQ.map((item, index) => (
				<React.Fragment key={`category-${item.name}-${index}`}>
					<header key={`faq-header-${index}`} className="faq-block-header">
						{item.name}
					</header>
					{item.questions ? (
						<FaqBlock
							key={item.name + index}
							questions={item.questions}
							expandIconPosition="right"
							maxCount={3}
							linkCategory={`/faq/${item.name.toLowerCase().replace(/[&]/g, 'and').replace(/\s+/g, '-')}`}
							category={item}
							showCategory={showCategory}
						/>
					) : (
						<div className="faq-block__dummy">Woof! We were not able to find a match. Please try another search.</div>
					)}
				</React.Fragment>
			))}
		</>
	);
};

const CategoryQuestions: React.FC<CategoryQuestionsProps> = ({
	searchQuery,
	categoriesFAQ,
	currentCategoryFAQ,
	showCategory,
}) => {
	const [allQuestions, setAllQuestions] = React.useState<Array<Questions>>([]);

	const context = useItemsProviderContext<Questions>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { items, loading },
		actions: { handleChange },
	} = context;

	React.useEffect(() => {
		if (currentCategoryFAQ?.name) {
			handleChange({
				text: searchQuery || '',
			});
		}
	}, [currentCategoryFAQ, searchQuery]);

	React.useEffect(() => {
		if (categoriesFAQ.length) {
			const questions: Array<Questions> = categoriesFAQ
				.map((category) => category.questions)
				.flat()
				.filter((question) => question.name.includes(searchQuery || '') || question.text.includes(searchQuery || ''));
			setAllQuestions(questions);
		}
	}, [categoriesFAQ, searchQuery]);

	return (
		<section className="faq-block faq-page">
			<div className="faq-block-body">
				{!loading ? (
					allQuestions?.length ? (
						<div className="faq-block-content">
							{currentCategoryFAQ && currentCategoryFAQ.name ? (
								<>
									<CertainCategoryFAQ currentCategoryFAQ={currentCategoryFAQ} questions={items} />
									<LoadMoreButton visible={!loading} className="btn btn-lg btn-secondary btn-load-more" />
								</>
							) : (
								!searchQuery?.length ? (
									<AllCategoriesFAQ categoriesFAQ={categoriesFAQ} showCategory={showCategory} />
								) : (
									<>
										<FaqBlock questions={allQuestions} expandIconPosition="right" visibleMore />
									</>
								)
							)}
						</div>
					) : (
						<div className="faq-block__dummy">Woof! We were not able to find a match. Please try another search.</div>
					)
				) : (
					<BounceLoader />
				)}
			</div>
		</section>
	);
};

const FAQ: React.FC<FRFaqProps> = ({
	setCategoriesFAQ,
	currentCategoryFAQ,
	setCurrentCategoryFAQ,
	searchQuery,
}) => {
	const params = useParams<{ id: string | undefined }>();
	const [loadingSSR, setLoadingSSR] = React.useState<boolean>(true);

	const context = useItemsProviderContext<IssuesId>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { loading, error, items },
		actions: { handleChange },
	} = context;

	const showCategory = (category) => {
		setCurrentCategoryFAQ(category);
		window.scrollTo(0, 0);
		document.querySelector('#react-app')?.scrollTo(0, 0);
	};

	React.useEffect(() => {
		setLoadingSSR(false);

		handleChange().then((res) => {
			setCategoriesFAQ(res?.list || []);
		});
	}, []);

	React.useEffect(() => {
		if (items.length && params.id) {
			if (params.id && !currentCategoryFAQ?.name?.length) {
				items.forEach((item) => {
					if (item.name.replace(/\s+/g, '-').replace('&', 'and').toLowerCase() === params.id) {
						showCategory(item);
					}
				});
			}
		}
	}, [items]);

	return (
		<>
			{params.id && currentCategoryFAQ?.questions?.length && (
				<Helmet key={`faq-schema-${params.id}`}>
					<script type="application/ld+json">
						{JSON.stringify({
							'@context': 'https://schema.org',
							'@type': 'FAQPage',
							mainEntity: currentCategoryFAQ.questions.map((question) => ({
								'@type': 'Question',
								name: question.name,
								acceptedAnswer: {
									'@type': 'Answer',
									text: question.text,
								},
							})),
						})}
					</script>
				</Helmet>
			)}

			<ItemsProvider<Questions>
				type={`faq/${currentCategoryFAQ?.name ? currentCategoryFAQ.name : ''}`}
				loadRequest={`faq/${currentCategoryFAQ?.name ? currentCategoryFAQ.name : ''}`}
				pagination={{ pageSize: 20, total: 0 }}
			>
				{!loadingSSR && !loading ? (
					!error.length ? (
						<CategoryQuestions
							searchQuery={searchQuery}
							categoriesFAQ={items}
							currentCategoryFAQ={currentCategoryFAQ}
							showCategory={showCategory}
						/>
					) : <div className="faq-block__dummy">Woof! We were not able to find a match. Please try another search.</div>
				) : <BounceLoader />}
			</ItemsProvider>
		</>
	);
};

const FRFaq: React.FC<FRFaqProps> = ({
	setCategoriesFAQ,
	currentCategoryFAQ,
	setCurrentCategoryFAQ,
	searchQuery,
}) => {
	return (
		<HelmetProvider>
			<CustomRequestProvider>
				<ItemsProvider<IssuesId>
					type="faqCategories"
					loadRequest="faqCategories"
				>
					<FAQ
						setCategoriesFAQ={setCategoriesFAQ}
						currentCategoryFAQ={currentCategoryFAQ}
						setCurrentCategoryFAQ={setCurrentCategoryFAQ}
						searchQuery={searchQuery}
					/>
				</ItemsProvider>
			</CustomRequestProvider>
		</HelmetProvider>
	);
};

export default FRFaq;
